import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

const getIban = (bank) => bank?.ibanNo ?? bank?.iBANNo ?? bank?.ifscCode;
const getBranch = (bank) => bank?.branchAddress ?? bank?.branchName;

const getCurrency = (bank) => {
  const iban = String(getIban(bank) ?? "").trim().toUpperCase();
  const match = iban.match(/\d([A-Z]{3})$/);
  return match?.[1] || "";
};

const getBankLabel = (bank) => {
  const name = bank?.bankName || "Bank";
  const currency = getCurrency(bank);
  return currency ? `${name} - ${currency}` : name;
};

const copyToClipboard = async (value) => {
  const text = String(value ?? "").trim();
  if (!text) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {}

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch (_) {
    return false;
  }
};

const CopyIcon = ({ copied }) =>
  copied ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="h-5 w-5 text-green-600"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  ) : (
    <svg
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-[#2F2B3D]"
    >
      <path
        d="M11 4V4C11 3.068 11 2.602 10.848 2.235A2 2 0 0 0 9.765 1.152C9.398 1 8.932 1 8 1H5C3.114 1 2.172 1 1.586 1.586C1 2.172 1 3.114 1 5V8C1 8.932 1 9.398 1.152 9.765A2 2 0 0 0 2.235 10.848C2.602 11 3.068 11 4 11V11"
        stroke="currentColor"
        strokeOpacity="0.78"
        strokeWidth="1.5"
      />
      <rect
        x="4"
        y="4"
        width="10"
        height="10"
        rx="2"
        stroke="currentColor"
        strokeOpacity="0.78"
        strokeWidth="1.5"
      />
    </svg>
  );

const BankField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  if (value === null || value === undefined || value === "") return null;

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const didCopy = await copyToClipboard(value);
    if (!didCopy) {
      toast.error("Unable to copy. Please try again.");
      return;
    }
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex w-full items-start justify-between gap-3 rounded-lg border border-[#E8E8E8] bg-white px-3 py-2.5 text-left transition-colors hover:border-primary"
    >
      <div className="min-w-0">
        <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-[#8B8B8B]">
          {label}
        </p>
        <p className="break-all text-sm font-semibold text-[#2F2B3D]">{value}</p>
      </div>
      <span className="mt-0.5 shrink-0">
        <CopyIcon copied={copied} />
      </span>
    </button>
  );
};

const ActiveBankDetails = ({ banks }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  useEffect(() => {
    if (!banks?.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((current) =>
      banks.some((bank) => bank?.id === current) ? current : banks[0]?.id,
    );
  }, [banks]);

  const selectedBank = useMemo(
    () => banks?.find((bank) => bank?.id === selectedId) || banks?.[0],
    [banks, selectedId],
  );

  const copyAllDetails = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!selectedBank) return;

    const lines = [
      selectedBank.bankName && `Bank Name: ${selectedBank.bankName}`,
      selectedBank.accountHolderName &&
        `Account Holder: ${selectedBank.accountHolderName}`,
      selectedBank.accountNumber &&
        `Account Number: ${selectedBank.accountNumber}`,
      getIban(selectedBank) && `IBAN No: ${getIban(selectedBank)}`,
      selectedBank.swiftCode && `Swift Code: ${selectedBank.swiftCode}`,
      getBranch(selectedBank) && `Branch Address: ${getBranch(selectedBank)}`,
    ].filter(Boolean);

    const didCopy = await copyToClipboard(lines.join("\n"));
    if (!didCopy) {
      toast.error("Unable to copy. Please try again.");
      return;
    }
    setCopiedAll(true);
    toast.success("Bank details copied");
    setTimeout(() => setCopiedAll(false), 1500);
  };

  if (!banks?.length) {
    return (
      <p className="text-sm text-gray-500">
        No active bank details are available. Please try again later.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {banks.length > 1 && (
        <div>
          <p className="mb-2 text-sm font-medium text-[#6B6B6B]">
            Choose a bank to deposit
          </p>
          <div className="flex flex-wrap gap-2">
            {banks.map((bank) => {
              const isSelected = bank?.id === selectedBank?.id;
              return (
                <button
                  key={bank?.id}
                  type="button"
                  onClick={() => setSelectedId(bank?.id)}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                    isSelected
                      ? "border-primary bg-primary text-white shadow-[0_6px_16px_rgba(47,91,234,0.18)]"
                      : "border-[#E8E8E8] bg-white text-[#2F2B3D] hover:border-primary hover:text-primary"
                  }`}
                >
                  {getBankLabel(bank)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selectedBank && (
        <div className="rounded-xl border border-primary/20 bg-[#F7F9FF] p-4">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-lg font-semibold text-[#2F2B3D]">
                {getBankLabel(selectedBank)}
              </p>
              <p className="text-sm text-[#6B6B6B]">
                Tap a value to copy it, or copy all details at once.
              </p>
            </div>
            <button
              type="button"
              onClick={copyAllDetails}
              className="inline-flex items-center gap-2 rounded-lg border border-primary bg-white px-3 py-1.5 text-sm font-semibold text-primary hover:bg-[#E8F1FF] hover:text-primary focus:bg-[#E8F1FF] focus:text-primary focus:outline-none active:bg-[#E8F1FF] active:text-primary"
            >
              {copiedAll ? "Copied" : "Copy all details"}
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <BankField label="Bank Name" value={selectedBank.bankName} />
            <BankField
              label="Account Holder"
              value={selectedBank.accountHolderName}
            />
            <BankField
              label="Account Number"
              value={selectedBank.accountNumber}
            />
            <BankField label="IBAN No" value={getIban(selectedBank)} />
            <BankField label="Swift Code" value={selectedBank.swiftCode} />
            <BankField label="Branch Address" value={getBranch(selectedBank)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveBankDetails;
